import React, { useState, useEffect } from 'react';
import '../styles/SiteContent.css';

const IMG_BASE = 'https://cal.mazbluu.com';

// Card sections grouped for display. 'withBody' controls whether the description
// fields show (amenities/rooms have descriptions; list items are title-only).
const CARD_GROUPS = [
  { section: 'amenities', label: 'El Condominio — feature cards', withBody: true, withPhoto: true },
  { section: 'rooms', label: 'Recámaras — room cards', withBody: true, withPhoto: true },
  { section: 'amenities_list', label: 'Amenidades nivel resort — checklist', withBody: false, withPhoto: false },
  { section: 'security', label: 'Seguridad — cards', withBody: true, withPhoto: false },
  { section: 'nearby_walk', label: 'Cercanos — walking distance', withBody: false, withPhoto: false },
  { section: 'nearby_drive', label: 'Cercanos — 10 minutes', withBody: false, withPhoto: false },
  { section: 'nearby_shop', label: 'Cercanos — supermarkets', withBody: false, withPhoto: false }
];

const blankDraft = (section) => ({ section, titleEs: '', titleEn: '', bodyEs: '', bodyEn: '', photoId: null });

export default function SiteContent() {
  const [tab, setTab] = useState('cards'); // 'cards' | 'text'
  const [cards, setCards] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [pageText, setPageText] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(blankDraft('amenities'));
  const token = localStorage.getItem('token');

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, p, t] = await Promise.all([
        fetch('/api/content', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/gallery', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/content/text', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (c.ok) setCards((await c.json()).cards || []);
      if (p.ok) setPhotos((await p.json()).photos || []);
      if (t.ok) setPageText((await t.json()).text || []);
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 2500); };

  // ---- cards ----
  const startNew = (section) => { setEditing('new:' + section); setDraft(blankDraft(section)); };
  const startEdit = (card) => {
    setEditing(card.id);
    setDraft({ section: card.section, titleEs: card.titleEs || '', titleEn: card.titleEn || '',
      bodyEs: card.bodyEs || '', bodyEn: card.bodyEn || '', photoId: card.photoId || null });
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!draft.titleEs && !draft.titleEn) { setError('Add a title'); return; }
    setError('');
    const isNew = String(editing).startsWith('new:');
    try {
      const res = await fetch(isNew ? '/api/content' : `/api/content/${editing}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(draft)
      });
      if (res.ok) { flash('Saved'); setEditing(null); loadAll(); }
      else { const e = await res.json(); setError(e.error || 'Save failed'); }
    } catch (err) { setError('Error: ' + err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const res = await fetch(`/api/content/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { flash('Deleted'); loadAll(); }
    } catch (err) { setError('Error: ' + err.message); }
  };

  const move = async (section, index, dir) => {
    const inSec = cards.filter(c => c.section === section);
    const t = index + dir;
    if (t < 0 || t >= inSec.length) return;
    const reordered = [...inSec];
    [reordered[index], reordered[t]] = [reordered[t], reordered[index]];
    setCards(cards.map(c => c.section === section ? reordered[inSec.findIndex(x => x.id === c.id)] || c : c));
    try {
      await fetch(`/api/content/section/${section}/order`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderedIds: reordered.map(c => c.id) })
      });
      loadAll();
    } catch (err) { setError('Error: ' + err.message); loadAll(); }
  };

  // ---- page text ----
  const saveText = async (key, valueEs, valueEn) => {
    try {
      const res = await fetch(`/api/content/text/${key}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ valueEs, valueEn })
      });
      if (res.ok) flash('Saved');
      else setError('Save failed');
    } catch (err) { setError('Error: ' + err.message); }
  };

  if (loading) return <div style={{ padding: 20 }}>Loading…</div>;

  const groupConf = (section) => CARD_GROUPS.find(g => g.section === section) || { withBody: true, withPhoto: true };

  const renderEditor = () => {
    const conf = groupConf(draft.section);
    return (
      <div className="sc-editor">
        <h4>{String(editing).startsWith('new:') ? 'New item' : 'Edit item'}</h4>
        <div className="sc-row">
          <div><label>Title (Español)</label><input value={draft.titleEs} onChange={e => setDraft({ ...draft, titleEs: e.target.value })} /></div>
          <div><label>Title (English)</label><input value={draft.titleEn} onChange={e => setDraft({ ...draft, titleEn: e.target.value })} /></div>
        </div>
        {conf.withBody && (
          <div className="sc-row">
            <div><label>Description (Español)</label><textarea value={draft.bodyEs} onChange={e => setDraft({ ...draft, bodyEs: e.target.value })} /></div>
            <div><label>Description (English)</label><textarea value={draft.bodyEn} onChange={e => setDraft({ ...draft, bodyEn: e.target.value })} /></div>
          </div>
        )}
        {conf.withPhoto && (
          <>
            <label>Photo (optional — from your gallery)</label>
            <div className="sc-photo-picker">
              <div className={`sc-photo-opt ${!draft.photoId ? 'sel' : ''}`} onClick={() => setDraft({ ...draft, photoId: null })}>None</div>
              {photos.map(p => (
                <div key={p.id} className={`sc-photo-opt ${draft.photoId === p.id ? 'sel' : ''}`} onClick={() => setDraft({ ...draft, photoId: p.id })}>
                  <img src={`${IMG_BASE}${p.thumbUrl}`} alt="" />
                </div>
              ))}
            </div>
          </>
        )}
        <div className="sc-editor-actions">
          <button className="sc-save" onClick={save}>Save</button>
          <button className="sc-cancel" onClick={cancel}>Cancel</button>
        </div>
      </div>
    );
  };

  // group page text by group_label for display
  const textGroups = {};
  pageText.forEach(t => { (textGroups[t.group_label] = textGroups[t.group_label] || []).push(t); });

  return (
    <div className="site-content">
      <h2>Site Content</h2>
      <p>Manage everything shown on the public site — cards, lists, and all section text. Changes appear on mazbluu.com immediately.</p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="sc-tabs">
        <button className={`sc-tab ${tab === 'cards' ? 'active' : ''}`} onClick={() => setTab('cards')}>Cards & Lists</button>
        <button className={`sc-tab ${tab === 'text' ? 'active' : ''}`} onClick={() => setTab('text')}>Page Text</button>
      </div>

      {tab === 'cards' && CARD_GROUPS.map(g => {
        const inSec = cards.filter(c => c.section === g.section);
        return (
          <div key={g.section} className="sc-section">
            <div className="sc-section-head">
              <h3>{g.label}</h3>
              <button className="sc-add" onClick={() => startNew(g.section)}>+ Add</button>
            </div>
            {editing === 'new:' + g.section && renderEditor()}
            {inSec.length === 0 ? <p className="sc-empty">No items yet.</p> : inSec.map((card, idx) => (
              <div key={card.id}>
                {editing === card.id ? renderEditor() : (
                  <div className="sc-card">
                    {card.thumbUrl && <img className="sc-card-thumb" src={`${IMG_BASE}${card.thumbUrl}`} alt="" />}
                    <div className="sc-card-body">
                      <strong>{card.titleEs}</strong>
                      <span className="sc-card-en">{card.titleEn}</span>
                      {card.bodyEs && <p>{card.bodyEs}</p>}
                    </div>
                    <div className="sc-card-actions">
                      <button disabled={idx === 0} onClick={() => move(g.section, idx, -1)}>▲</button>
                      <button disabled={idx === inSec.length - 1} onClick={() => move(g.section, idx, 1)}>▼</button>
                      <button className="sc-edit" onClick={() => startEdit(card)}>Edit</button>
                      <button className="sc-del" onClick={() => remove(card.id)}>✕</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {tab === 'text' && Object.keys(textGroups).map(group => (
        <div key={group} className="sc-section">
          <h3>{group}</h3>
          {textGroups[group].map(t => (
            <TextRow key={t.text_key} item={t} onSave={saveText} />
          ))}
        </div>
      ))}
    </div>
  );
}

function TextRow({ item, onSave }) {
  const [es, setEs] = useState(item.value_es || '');
  const [en, setEn] = useState(item.value_en || '');
  const dirty = es !== (item.value_es || '') || en !== (item.value_en || '');
  return (
    <div className="sc-text-row">
      <div className="sc-text-label">{item.label}</div>
      <div className="sc-row">
        <div><label>Español</label><input value={es} onChange={e => setEs(e.target.value)} /></div>
        <div><label>English</label><input value={en} onChange={e => setEn(e.target.value)} /></div>
      </div>
      <button className="sc-text-save" disabled={!dirty} onClick={() => onSave(item.text_key, es, en)}>
        {dirty ? 'Save' : 'Saved'}
      </button>
    </div>
  );
}
